import React, { useState } from 'react';
import { RankRequirement } from '../types';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, setDoc, updateDoc, doc, deleteDoc, writeBatch } from 'firebase/firestore';
import { Plus, Edit2, Trash2, ShieldAlert, Table as TableIcon, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { RANKS as DEFAULT_RANKS } from '../constants';
import LCARSConfirmationModal from './LCARSConfirmationModal';

interface RankManagerProps {
  ranks: RankRequirement[];
}

export default function RankManager({ ranks }: RankManagerProps) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRank, setEditingRank] = useState<RankRequirement | null>(null);
  const [formData, setFormData] = useState<Partial<RankRequirement>>({
    otsRequired: false,
    occRequired: false,
    minMonths: 0,
    minEvents: 0,
    minPoints: 0,
    order: 0
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant: 'danger' | 'info';
  }>({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: () => {},
    variant: 'danger'
  });

  const askConfirmation = (title: string, description: string, onConfirm: () => void, variant: 'danger' | 'info' = 'danger') => {
    setConfirmDialog({
      isOpen: true,
      title,
      description,
      onConfirm,
      variant
    });
  };

  const handleBootstrap = async () => {
    askConfirmation(
      'System Recall: Bootstrap Ranks',
      'This will populate the database with default Starfleet ranks. Existing custom ranks will not be overwritten.',
      async () => {
        try {
          const batch = writeBatch(db);
          DEFAULT_RANKS.forEach((rank, index) => {
            const docRef = doc(collection(db, 'ranks'), rank.id);
            batch.set(docRef, { ...rank, order: index * 10 });
          });
          await batch.commit();
          toast.success('Ranks core initialized');
        } catch (error) {
          handleFirestoreError(error, OperationType.WRITE, 'ranks');
        }
      },
      'info'
    );
  };

  const handleEdit = (rank: RankRequirement) => {
    setEditingRank(rank);
    setFormData(rank);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    askConfirmation(
      'Confirm Rank Deletion',
      'Are you sure you want to delete this rank? Existing members with this rank may need manual updates to maintain their status manifest.',
      async () => {
        try {
          await deleteDoc(doc(db, 'ranks', id));
          toast.success('Rank removed from records');
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, 'ranks');
        }
      }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!formData.id || !formData.name || !formData.shortName) {
        toast.error('Missing required fields');
        return;
      }

      // Clean ID format (uppercase, trimmed)
      const targetId = formData.id.trim().toUpperCase();
      const targetName = formData.name.trim();

      // Duplicate prevention checks
      if (!editingRank) {
        const isDuplicateId = ranks.some(r => r.id.toUpperCase() === targetId);
        const isDuplicateName = ranks.some(r => r.name.toLowerCase().trim() === targetName.toLowerCase());
        if (isDuplicateId) {
          toast.error(`Rank ID "${targetId}" already exists.`);
          return;
        }
        if (isDuplicateName) {
          toast.error(`Rank Name "${targetName}" already exists.`);
          return;
        }
      } else {
        const isDuplicateName = ranks.some(r => r.id !== editingRank.id && r.name.toLowerCase().trim() === targetName.toLowerCase());
        if (isDuplicateName) {
          toast.error(`Rank Name "${targetName}" already exists on another rank.`);
          return;
        }
      }

      const dbData = {
        ...Object.fromEntries(Object.entries(formData).filter(([k, v]) => v !== undefined)),
        id: targetId,
        name: targetName
      };

      if (editingRank) {
        await updateDoc(doc(db, 'ranks', editingRank.id), dbData);
        toast.success('Rank updated');
      } else {
        await setDoc(doc(db, 'ranks', targetId), dbData);
        toast.success('New rank commissioned');
      }
      setIsFormOpen(false);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'ranks');
    }
  };

  return (
    <div className="bg-[var(--background)] border-l-8 border-lcars-gold pl-4 lg:pl-6 py-2">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          <span className="text-xl font-bold text-lcars-gold uppercase">Rank Management</span>
          {ranks.length === 0 && (
            <button 
              onClick={handleBootstrap}
              className="lcars-pill bg-lcars-red text-[var(--foreground)] px-4 py-1 text-xs font-bold flex items-center gap-2"
            >
              <ShieldAlert className="w-4 h-4" /> BOOTSTRAP RANKS
            </button>
          )}
        </div>
        <button 
          onClick={() => { setEditingRank(null); setFormData({ order: ranks.length * 10, otsRequired: false, occRequired: false }); setIsFormOpen(true); }}
          className="lcars-pill bg-lcars-blue text-black px-6 py-1.5 text-sm font-bold flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> ADD NEW RANK
        </button>
      </div>

      <div className="overflow-auto border-t border-lcars-gold/20">
        <table className="w-full border-collapse">
          <thead>
            <tr className="text-lcars-gold text-[0.7rem] font-bold tracking-widest uppercase">
              <th className="text-left p-4 border-b-2 border-lcars-gold/30">ORDER</th>
              <th className="text-left p-4 border-b-2 border-lcars-gold/30">ID</th>
              <th className="text-left p-4 border-b-2 border-lcars-gold/30">NAME</th>
              <th className="text-left p-4 border-b-2 border-lcars-gold/30">REQUIREMENTS</th>
              <th className="text-right p-4 border-b-2 border-lcars-gold/30">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-lcars-gold/10">
            {ranks.map(rank => (
              <tr key={rank.id} className="hover:bg-lcars-gold/5 transition-colors group">
                <td className="p-4 font-mono text-lcars-gold">{rank.order}</td>
                <td className="p-4 font-bold">{rank.id}</td>
                <td className="p-4">
                  <div className="font-bold text-lg">{rank.name}</div>
                  <div className="text-[0.6rem] text-[var(--foreground)]/40">{rank.shortName}</div>
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {rank.minMonths! > 0 && <span className="bg-lcars-blue/20 text-lcars-blue px-2 py-0.5 rounded text-[0.65rem] font-bold">{rank.minMonths}M Tenure</span>}
                    {rank.minPoints! > 0 && <span className="bg-lcars-gold/20 text-lcars-gold px-2 py-0.5 rounded text-[0.65rem] font-bold">{rank.minPoints} Points</span>}
                    {rank.minEvents! > 0 && <span className="bg-lcars-purple/20 text-lcars-purple px-2 py-0.5 rounded text-[0.65rem] font-bold">{rank.minEvents} Missions</span>}
                    {rank.otsRequired && <span className="bg-lcars-tan/20 text-lcars-tan px-2 py-0.5 rounded text-[0.65rem] font-bold">OTS</span>}
                    {rank.occRequired && <span className="bg-lcars-blue/20 text-lcars-blue px-2 py-0.5 rounded text-[0.65rem] font-bold">OCC</span>}
                    {!rank.minMonths && !rank.minPoints && !rank.minEvents && !rank.otsRequired && !rank.occRequired && <span className="text-[var(--foreground)]/20 text-[0.65rem] italic">No Requirements</span>}
                  </div>
                </td>
                <td className="p-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button 
                      onClick={() => handleEdit(rank)}
                      className="p-2 bg-lcars-purple/20 text-lcars-purple rounded-full hover:bg-lcars-purple hover:text-black transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(rank.id)}
                      className="p-2 bg-lcars-red/20 text-lcars-red rounded-full hover:bg-lcars-red hover:text-[var(--foreground)] transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="bg-card-bg border border-border-color text-[var(--foreground)] max-w-2xl font-lcars p-0 overflow-hidden">
          <DialogHeader className="p-6 bg-white/5 border-b border-border-color">
            <DialogTitle className="uppercase text-lcars-gold tracking-widest font-bold">
              {editingRank ? 'Modify Rank' : 'New Rank Specification'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[0.7rem] uppercase font-bold text-[var(--foreground)]/40 tracking-widest">Rank ID (e.g. ADM)</Label>
                <Input 
                  value={formData.id || ''} 
                  onChange={e => setFormData({ ...formData, id: e.target.value.toUpperCase() })}
                  disabled={!!editingRank}
                  className="bg-[var(--background)] border-border-color focus:border-lcars-blue"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[0.7rem] uppercase font-bold text-[var(--foreground)]/40 tracking-widest">Sort Order (number)</Label>
                <Input 
                  type="number"
                  value={formData.order || 0} 
                  onChange={e => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  className="bg-[var(--background)] border-border-color focus:border-lcars-blue"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[0.7rem] uppercase font-bold text-[var(--foreground)]/40 tracking-widest">Full Name</Label>
                <Input 
                  value={formData.name || ''} 
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[var(--background)] border-border-color focus:border-lcars-blue"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[0.7rem] uppercase font-bold text-[var(--foreground)]/40 tracking-widest">Display Abbr</Label>
                <Input 
                  value={formData.shortName || ''} 
                  onChange={e => setFormData({ ...formData, shortName: e.target.value })}
                  className="bg-[var(--background)] border-border-color focus:border-lcars-blue"
                  required
                />
              </div>
            </div>

            <div className="pt-4 border-t border-white/10">
              <p className="text-[0.7rem] uppercase font-bold text-lcars-gold tracking-widest mb-4">Promotion Requirements</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-[0.7rem] uppercase font-bold text-[var(--foreground)]/40 tracking-widest">Min Months</Label>
                  <Input 
                    type="number"
                    value={formData.minMonths || 0} 
                    onChange={e => setFormData({ ...formData, minMonths: parseInt(e.target.value) || 0 })}
                    className="bg-[var(--background)] border-border-color focus:border-lcars-blue"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[0.7rem] uppercase font-bold text-[var(--foreground)]/40 tracking-widest">Min Events</Label>
                  <Input 
                    type="number"
                    value={formData.minEvents || 0} 
                    onChange={e => setFormData({ ...formData, minEvents: parseInt(e.target.value) || 0 })}
                    className="bg-[var(--background)] border-border-color focus:border-lcars-blue"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[0.7rem] uppercase font-bold text-[var(--foreground)]/40 tracking-widest">Min Points</Label>
                  <Input 
                    type="number"
                    value={formData.minPoints || 0} 
                    onChange={e => setFormData({ ...formData, minPoints: parseInt(e.target.value) || 0 })}
                    className="bg-[var(--background)] border-border-color focus:border-lcars-blue"
                  />
                </div>
              </div>

              <div className="flex gap-8 mt-6">
                <div className="flex items-center space-x-3">
                  <Checkbox 
                    id="ots-req" 
                    checked={formData.otsRequired} 
                    onCheckedChange={v => setFormData({ ...formData, otsRequired: !!v })}
                    className="border-lcars-blue"
                  />
                  <Label htmlFor="ots-req" className="text-sm font-bold uppercase tracking-wider cursor-pointer">OTS REQ</Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox 
                    id="occ-req" 
                    checked={formData.occRequired} 
                    onCheckedChange={v => setFormData({ ...formData, occRequired: !!v })}
                    className="border-lcars-blue"
                  />
                  <Label htmlFor="occ-req" className="text-sm font-bold uppercase tracking-wider cursor-pointer">OCC REQ</Label>
                </div>
              </div>
            </div>

            <DialogFooter className="pt-6 border-t border-white/10 flex gap-4">
              <button 
                type="button" 
                onClick={() => setIsFormOpen(false)}
                className="lcars-pill bg-lcars-tan text-black px-6 py-2 text-sm font-bold"
              >
                DISCARD
              </button>
              <button 
                type="submit" 
                className="lcars-pill bg-lcars-blue text-black px-10 py-2 text-sm font-bold flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> COMMISSION RANK
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <LCARSConfirmationModal
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        variant={confirmDialog.variant}
      />
    </div>
  );
}
